import './EngagementMeetingCancelModal.scss';
import React, { useEffect, useState } from 'react';
import {
    Button,
    FormGroup,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';
import Rodal from '../../../../../components/Rodal/Rodal';
import createNotification from '../../../../../utils/createNotification';
import { useStateSelector } from '../../../../../store/selectors';
import { MeetingObject } from '../../../../../slices/marketplace/meetings/models/meeting-object.model';
import Loader from 'react-loaders';
import ButtonLoader from '../../../../../components/Layout/Buttons/ButtonLoader';
import { MeetingProposalStatus } from '../../../../../slices/marketplace/meetings/models/enums/meeting-proposal-status.enum';
import { MeetingFormat } from '../../../../../slices/marketplace/meetings/models/enums/meeting-format.enum';

interface EngagementMeetingCancelModalProps {
    isVisible: boolean;
    onClose: () => void;
    onCancel: () => void;
    isCancelling: boolean;
    engagementId: number;
    proposal: MeetingObject;
    proRata?: boolean;
    isBuyer: boolean;
    isSfas?: boolean;
}

const EngagementMeetingCancelModal = (
    props: EngagementMeetingCancelModalProps
) => {
    const axios = useStateSelector((state) => state.core.axios);

    const [isPopupLoading, setIsPopupLoading] = useState(true);
    const [cancellationRefundAmount, setCancellationRefundAmount] =
        useState(-1);

    const formatCurrency = (value: number) => {
        if (value == null) return '$0';
        const isWholeNumber = value % 1 === 0;
        return '$' + (isWholeNumber ? value.toString() : value.toFixed(2));
    };

    const isPendingProposal =
        !props.proposal.isCancelled &&
        props.proposal.isMeetingProposal === true &&
        (props.proposal.proposalStatus ===
            MeetingProposalStatus.PendingSellerAnswer ||
            props.proposal.proposalStatus ===
                MeetingProposalStatus.PendingBuyerAnswer);

    const hasPaidForMeeting = (props.proposal?.pricePaid ?? 0) > 0;

    const isProRataByEvent =
        (props.proposal?.format === MeetingFormat.Virtual &&
            !!props.proposal?.confirmedCancellationDeadlineHours) ||
        !!props.proposal?.confirmedCancellationDeadlineDays;

    useEffect(() => {
        if (!props.isBuyer || isPendingProposal || props.isSfas || !hasPaidForMeeting) {
            setIsPopupLoading(false);
            return;
        }

        axios
            .post(`/api/MarketplaceCommerce/MeetingFeeRefund/Amount`, {
                engagementId: props.engagementId,
                proposalId: props.proposal.meetingProposalId,
                proRata: props.proRata ?? isProRataByEvent,
                reason: undefined,
            })
            .then((response) => {
                if (response.status === 200) {
                    setCancellationRefundAmount(response.data);
                } else {
                    createNotification(
                        'An error occurred while fetching refund data',
                        'error'
                    );
                }
            })
            .catch((error) => {
                createNotification(
                    'An error occurred while fetching refund data',
                    'error'
                );
                props.onClose();
            })
            .finally(() => setIsPopupLoading(false));
    }, []);

    const renderLoader = () => {
        return (
            <div
                className="loader-container"
                style={{
                    width: '100%',
                    height: '10vw',
                }}>
                <div className="loader-container-inner">
                    <div className="text-center">
                        <Loader
                            active={isPopupLoading}
                            type="ball-scale-multiple"
                        />
                    </div>
                    <h6 className="mt-5">Loading your data...</h6>
                </div>
            </div>
        );
    };

    return (
        <Rodal
            visible={props.isVisible}
            onClose={props.onClose}
            width={498}
            centered
            className="engagement-meeting-cancellation-modal">
            <ModalHeader>
                {isPendingProposal
                    ? 'Cancel Meeting Proposal'
                    : 'Cancel Meeting'}
            </ModalHeader>
            <FormGroup>
                <ModalBody>
                    {isPopupLoading ? (
                        renderLoader()
                    ) : (
                        <div className="cancellation-modal-content">
                            <span>
                                {isPendingProposal
                                    ? 'Are you sure you want to cancel this meeting proposal?'
                                    : 'Are you sure you want to cancel this meeting?'}
                            </span>

                            {props.isBuyer && !props.isSfas && cancellationRefundAmount >= 0 ? (
                                <span>
                                    You will be refunded{' '}
                                    <b>
                                        {formatCurrency(
                                            cancellationRefundAmount
                                        )}
                                    </b>
                                    .
                                </span>
                            ) : (
                                <></>
                            )}
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        type="button"
                        className={'btn-cancel'}
                        onClick={() => {
                            setCancellationRefundAmount(-1);
                            props.onClose();
                        }}>
                        {'Cancel'}
                    </Button>
                    <ButtonLoader
                        disabled={
                            isPopupLoading && cancellationRefundAmount < 0
                        }
                        className={'btn btn-primary'}
                        buttonText={'Confirm'}
                        loaderButtonText={'Confirm'}
                        isLoading={props.isCancelling}
                        onClick={() => props.onCancel()}
                    />
                </ModalFooter>
            </FormGroup>
        </Rodal>
    );
};

export default EngagementMeetingCancelModal;
